import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  apagarPapel,
  atualizarPapel,
  concederPermissao,
  criarPapel,
  listarPapeis,
  revogarPermissao,
  type PapelAdmin,
  type Permissao,
} from '../api/admin'
import { useAuth } from '../state/AuthContext'

type Dialogo =
  | { tipo: 'criar'; nome: string; descricao: string }
  | { tipo: 'editar'; papel: PapelAdmin; nome: string; descricao: string }
  | { tipo: 'apagar'; papel: PapelAdmin }
  | null

/**
 * Papéis são configuráveis: cada um é um conjunto de permissões. O Admin é de sistema e
 * não se mexe. Ninguém concede uma permissão que não tem — a tela desabilita e o banco barra.
 */
export function PapeisPage() {
  const { pode } = useAuth()
  const [papeis, setPapeis] = useState<PapelAdmin[] | null>(null)
  const [permissoes, setPermissoes] = useState<Permissao[]>([])
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [dialogo, setDialogo] = useState<Dialogo>(null)

  const carregar = useCallback(async () => {
    try {
      const r = await listarPapeis()
      setPapeis(r.papeis)
      setPermissoes(r.permissoes)
    } catch (e) {
      setErro((e as Error).message)
    }
  }, [])

  useEffect(() => {
    carregar()
  }, [carregar])

  const alterar = async (acao: () => Promise<unknown>) => {
    setSalvando(true)
    try {
      await acao()
      setErro(null)
    } catch (e) {
      setErro((e as Error).message)
    } finally {
      await carregar()
      setSalvando(false)
    }
  }

  const confirmar = () => {
    const d = dialogo
    if (!d) return
    setDialogo(null)
    if (d.tipo === 'criar') alterar(() => criarPapel(d.nome, d.descricao))
    else if (d.tipo === 'editar') alterar(() => atualizarPapel(d.papel.id, { nome: d.nome.trim(), descricao: d.descricao.trim() || null }))
    else alterar(() => apagarPapel(d.papel.id))
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="flex-start" spacing={2} useFlexGap flexWrap="wrap">
        <Box sx={{ mr: 'auto' }}>
          <Typography variant="h5" sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, lineHeight: 1.2 }}>
            Papéis
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
            Cada papel é um conjunto de permissões; cada usuário tem um papel. O Admin é de sistema: tem tudo e não se
            altera. Você só consegue dar a um papel as permissões que você mesmo tem.
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => setDialogo({ tipo: 'criar', nome: '', descricao: '' })} sx={{ textTransform: 'none' }}>
          Novo papel
        </Button>
      </Stack>

      {erro ? (
        <Alert severity="error" onClose={() => setErro(null)}>
          {erro}
        </Alert>
      ) : null}

      {!papeis ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ opacity: salvando ? 0.6 : 1, transition: 'opacity 160ms ease' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Permissão</TableCell>
                {papeis.map((p) => (
                  <TableCell key={p.id} align="center" sx={{ minWidth: 120, verticalAlign: 'bottom' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {p.nome}
                    </Typography>
                    {p.sistema ? (
                      <Typography variant="caption" color="text.secondary">
                        sistema
                      </Typography>
                    ) : (
                      <Stack direction="row" justifyContent="center">
                        <Button
                          size="small"
                          onClick={() => setDialogo({ tipo: 'editar', papel: p, nome: p.nome, descricao: p.descricao ?? '' })}
                          sx={{ textTransform: 'none', minWidth: 0, px: 0.75 }}
                        >
                          Editar
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => setDialogo({ tipo: 'apagar', papel: p })}
                          sx={{ textTransform: 'none', minWidth: 0, px: 0.75 }}
                        >
                          Apagar
                        </Button>
                      </Stack>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {permissoes.map((perm) => (
                <TableRow key={perm.codigo} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {perm.descricao}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      {perm.codigo}
                    </Typography>
                  </TableCell>
                  {papeis.map((p) => {
                    const tem = p.permissoes.has(perm.codigo)
                    const semPoder = !tem && !pode(perm.codigo)
                    const motivo = p.sistema
                      ? 'O Admin tem todas as permissões e não se altera'
                      : semPoder
                        ? 'Você não tem esta permissão, então não pode concedê-la'
                        : ''
                    return (
                      <TableCell key={p.id} align="center">
                        <Tooltip title={motivo}>
                          <span>
                            <Checkbox
                              checked={tem}
                              disabled={salvando || p.sistema || semPoder}
                              onChange={() =>
                                alterar(() => (tem ? revogarPermissao(p.id, perm.codigo) : concederPermissao(p.id, perm.codigo)))
                              }
                              inputProps={{ 'aria-label': `${perm.descricao} — ${p.nome}` }}
                            />
                          </span>
                        </Tooltip>
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={Boolean(dialogo)} onClose={() => setDialogo(null)} fullWidth maxWidth="xs">
        {dialogo?.tipo === 'apagar' ? (
          <>
            <DialogTitle>Apagar o papel “{dialogo.papel.nome}”?</DialogTitle>
            <DialogContent>
              <Typography variant="body2">
                Só é possível apagar um papel que nenhum usuário tem. Se houver alguém com ele, troque o papel dessas
                pessoas antes.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogo(null)}>Cancelar</Button>
              <Button color="error" variant="contained" onClick={confirmar}>
                Apagar
              </Button>
            </DialogActions>
          </>
        ) : dialogo ? (
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault()
              if (dialogo.nome.trim()) confirmar()
            }}
          >
            <DialogTitle>{dialogo.tipo === 'criar' ? 'Novo papel' : 'Editar papel'}</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField autoFocus label="Nome" value={dialogo.nome} onChange={(e) => setDialogo({ ...dialogo, nome: e.target.value })} />
                <TextField
                  label="Descrição"
                  placeholder="Para que serve este papel"
                  value={dialogo.descricao}
                  onChange={(e) => setDialogo({ ...dialogo, descricao: e.target.value })}
                />
                {dialogo.tipo === 'criar' ? (
                  <Typography variant="caption" color="text.secondary">
                    O papel nasce sem permissões; marque as que ele deve ter na tabela.
                  </Typography>
                ) : null}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogo(null)}>Cancelar</Button>
              <Button type="submit" variant="contained" disabled={!dialogo.nome.trim()}>
                {dialogo.tipo === 'criar' ? 'Criar' : 'Salvar'}
              </Button>
            </DialogActions>
          </Box>
        ) : null}
      </Dialog>
    </Stack>
  )
}
